import React, { FunctionComponent } from 'react';
import { compact, filter, map, uniqBy } from 'lodash';
import {
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank,
} from '@mui/icons-material';
import { isEqual } from 'lodash';

import { Autocomplete, Checkbox, TextField, Typography } from '@mui/material';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';

import { Dropdown, ModelInstance, RowIdentifier } from '../../types/models';

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface IOutputsMultiSelectProps {
  /**
   * List of outputs to be populated
   */
  outputsList: RowIdentifier[];
  /**
   * List of outputs selected
   */
  preSelectedOutputs?: RowIdentifier[];
  /**
   * The onChange handler
   * @param output
   */
  handleOutputChange: (output: RowIdentifier[]) => void;
  /**
   * The modelInstance
   * This component will be disabled until a modelInstance is passed in
   */
  modelInstance?: ModelInstance;
  size?: 'small' | 'medium';
  hasError?: boolean;
  selectAll?: boolean;
}

const OutputsMultiSelect: FunctionComponent<IOutputsMultiSelectProps> = ({
  outputsList,
  preSelectedOutputs,
  handleOutputChange,
  modelInstance,
  size = 'medium',
  hasError,
  selectAll = false,
}) => {
  const [searchTerm, setSearchTerm] = React.useState('');

  const getSelectAllOption = () => {
    return {
      Name: Dropdown.Select_All,
      DimNumber: null,
      DisableInputEdit: false,
      DisableOutputAggregation: false,
      FormatKey: 0,
      GroupID: undefined,
      InputCategory: '',
      ModelInstanceID: modelInstance?.id || 0,
      PreviousGroupName: null,
      TabName: '',
      Type: 'Input' as const,
      created_at: '',
      id: 999999,
      value: 9999999,
    };
  };

  const filterOptions = (
    options: RowIdentifier[],
    { inputValue }: { inputValue: string }
  ) => {
    if (inputValue === '') {
      return options;
    }
    return options.filter((option) =>
      option.Name.toLowerCase().includes(inputValue.toLowerCase())
    );
  };

  const getSelectedFlag = (selected: boolean) => {
    if (preSelectedOutputs?.length === outputsList.length) {
      return true;
    } else {
      return selected;
    }
  };

  const handleChange = (
    event: React.ChangeEvent<{}>,
    value: RowIdentifier[]
  ) => {
    const target = event.target as HTMLElement;
    const isSelectAllClick =
      target.textContent === Dropdown.Select_All ||
      target.closest('li')?.textContent === Dropdown.Select_All;

    if (
      isSelectAllClick &&
      preSelectedOutputs?.length !== outputsList.length &&
      preSelectedOutputs?.length !==
        filterOptions(outputsList, { inputValue: searchTerm }).length
    ) {
      let filteredInputs = filterOptions(outputsList, {
        inputValue: searchTerm,
      });
      let options = [];
      if (preSelectedOutputs && searchTerm !== '') {
        options = uniqBy([...preSelectedOutputs, ...filteredInputs], 'Name');
        handleOutputChange(options);
      } else {
        options = uniqBy([...filteredInputs], 'Name');
        handleOutputChange(filteredInputs);
      }
      setSearchTerm('');
    } else if (
      isSelectAllClick &&
      preSelectedOutputs?.length === outputsList.length
    ) {
      handleOutputChange([]);
    } else {
      let newValue = filter(value, (v) => v.Name !== Dropdown.Select_All);
      handleOutputChange(newValue);
    }
  };

  const setSearchString = (event: React.FormEvent<HTMLInputElement>) => {
    const target = event.target as HTMLInputElement;
    const searchString = target.value;
    setSearchTerm(searchString);
  };

  return (
    <Autocomplete
      multiple
      value={preSelectedOutputs}
      onChange={(event, value) => handleChange(event, value)}
      disableCloseOnSelect
      options={outputsList || []}
      getOptionLabel={(option) => option.Name}
      isOptionEqualToValue={(option, value) => isEqual(option, value)}
      filterOptions={(filteredOptions, params) => {
        const filtered = filterOptions(filteredOptions, params);
        let selectAllOption = null;
        if (selectAll && filtered.length > 0) {
          selectAllOption = getSelectAllOption();
        }

        return compact([selectAllOption, ...filtered]) as RowIdentifier[];
      }}
      renderOption={(props, option, { inputValue, selected }) => {
        const matches = match(option.Name, inputValue, {
          insideWords: true,
          findAllOccurrences: true,
        });
        const parts = parse(option.Name, matches);
        return (
          <Typography component="li" {...props} noWrap>
            <Checkbox
              icon={icon}
              checkedIcon={checkedIcon}
              style={{ marginRight: 8 }}
              checked={selectAll ? getSelectedFlag(selected) : selected}
            />
            {map(parts, (part, index) => (
              <span
                key={index}
                style={{
                  fontWeight: part.highlight ? 700 : 400,
                  whiteSpace: 'pre',
                }}
              >
                {part.text}
              </span>
            ))}
          </Typography>
        );
      }}
      disabled={modelInstance === undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Select outputs"
          fullWidth
          size={size}
          error={hasError}
          onInput={setSearchString}
        />
      )}
    />
  );
};

export default OutputsMultiSelect;
